import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TableBody,
  TableContainer,
  TableRow,
  Chip,
  Grid,
  MenuItem,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DeleteOutline as DeleteIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  CheckCircleOutline as CheckCircleIcon,
} from '@mui/icons-material';
import { InventoryRequestsService } from '../../../api/services/InventoryRequestsService';
import { CreateInventoryRequestDto } from '../../../api/models/CreateInventoryRequestDto';
import { InventoryItem } from '../../../types/inventory';
import {
  useInventoryRequestModal,
  type RequestLocationOption,
} from './InventoryRequestModal.hooks';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { ConfirmationModal } from '../../ui/Modal';
import { CloseConfirmationModal } from '../../modals/confirmation';
import ProgressBar from '../../inventory/add-panel/ProgressBar';
import StepIndicators from '../../inventory/add-panel/StepIndicators';
import { StepConfig } from '../../inventory/add-panel/StepIndicators/StepIndicators.hooks';
import {
  PanelOverlay,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelContent,
  PanelActions,
  StepContent,
  ProductListContainer,
  ProductItem,
  ProductInfo,
  ProductCode,
  ProductName,
  ProductDetails,
  ProductDetailChip,
  RemoveButton,
  FormSection,
  FormLabel,
  FormSectionTitle,
  StyledTextField,
  StyledFormControl,
  SummaryContainer,
  SummaryTitle,
  SummaryGrid,
  SummaryCard,
  SummaryLabel,
  SummaryValueText,
  SummaryChip,
  SummaryNotesText,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  ProductsTableTitle,
  SectionTitle,
  PrimaryButton,
  SecondaryButton,
  LoadingContainer,
  LoadingText,
  ErrorAlert,
  StepsContainer,
  CompactProgressBarContainer,
  CompactStepIndicatorsContainer,
} from '../InventoryTransferModal/InventoryTransferModal.styles';

export interface InventoryRequestModalProps {
  open: boolean;
  onClose: () => void;
  selectedProductIds: string[];
  inventoryItems: InventoryItem[];
  locations: RequestLocationOption[];
  defaultDestinationLocationId?: number | null;
  onSuccess: () => void;
  onRemoveProduct?: (productId: string) => void;
}

const InventoryRequestModal: React.FC<InventoryRequestModalProps> = ({
  open,
  onClose,
  selectedProductIds,
  inventoryItems,
  locations,
  defaultDestinationLocationId,
  onSuccess,
  onRemoveProduct,
}) => {
  const {
    activeStep,
    loading,
    setLoading,
    selectedProducts,
    sourceLocationName,
    hasMixedOrigins,
    destinationLocationId,
    availableDestinations,
    isSameAsSource,
    notes,
    error,
    setError,
    handleNext,
    handleBack,
    handleReset,
    handleDestinationChange,
    handleNotesChange,
    handleRemoveProduct,
    canProceedToNext,
    getDestinationLocationName,
  } = useInventoryRequestModal({
    open,
    selectedProductIds,
    inventoryItems,
    locations,
    defaultDestinationLocationId,
    onRemoveProduct,
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const handleCloseModal = () => {
    if (!loading) {
      setShowCloseConfirmation(true);
    }
  };

  const handleConfirmClose = () => {
    handleReset();
    setShowCloseConfirmation(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  useEscapeKey(
    () => {
      if (open && !loading && !showCloseConfirmation) {
        setShowCloseConfirmation(true);
      }
    },
    open && !loading && !showCloseConfirmation,
  );

  const requestSteps: StepConfig[] = useMemo(
    () => [
      {
        id: 1,
        title: 'Piezas seleccionadas',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
        ),
        color: { active: 'bg-purple-600', completed: 'bg-purple-100', inactive: 'bg-gray-200' },
        textColor: {
          active: 'text-white',
          completed: 'text-purple-800',
          inactive: 'text-gray-700',
        },
      },
      {
        id: 2,
        title: 'Ubicación destino',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        ),
        color: { active: 'bg-blue-600', completed: 'bg-blue-100', inactive: 'bg-gray-200' },
        textColor: { active: 'text-white', completed: 'text-blue-800', inactive: 'text-gray-700' },
      },
      {
        id: 3,
        title: 'Confirmar solicitud',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ),
        color: { active: 'bg-emerald-500', completed: 'bg-emerald-100', inactive: 'bg-gray-200' },
        textColor: {
          active: 'text-white',
          completed: 'text-emerald-800',
          inactive: 'text-gray-700',
        },
      },
    ],
    [],
  );

  const currentStep = activeStep + 1;
  const totalSteps = 3;

  const handleSubmit = () => {
    if (selectedProducts.length === 0) {
      setError('Debe seleccionar al menos una pieza para solicitar.');
      return;
    }
    if (hasMixedOrigins) {
      setError('Todas las piezas deben compartir la misma ubicación de origen.');
      return;
    }
    if (destinationLocationId === '' || Number(destinationLocationId) <= 0) {
      setError('Debe seleccionar una ubicación de destino.');
      return;
    }
    setShowConfirmModal(true);
  };

  const executeRequest = async () => {
    setLoading(true);
    setError(null);
    setShowConfirmModal(false);

    try {
      const numericIds = selectedProducts
        .map((product) => product.originalId)
        .filter((id): id is number => id != null);

      if (numericIds.length !== selectedProducts.length) {
        throw new Error('No se pudieron resolver los IDs de las piezas seleccionadas.');
      }

      const payload: CreateInventoryRequestDto = {
        warehouseInventoryProductIds: numericIds,
        destinationLocationId: Number(destinationLocationId),
      };
      if (notes.trim()) {
        payload.notes = notes.trim();
      }

      await InventoryRequestsService.inventoryRequestsControllerCreate(payload);

      onSuccess();
      handleReset();
      onClose();
    } catch (err: unknown) {
      console.error('Error al crear la solicitud', err);
      const errorMessage =
        (err as { body?: { message?: string }; message?: string })?.body?.message ??
        (err as { message?: string })?.message ??
        'No se pudo procesar la solicitud. Intente de nuevo.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <StepContent>
            {selectedProducts.length === 0 ? (
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  backgroundColor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === 'dark' ? 0.15 : 0.08,
                  ),
                  borderRadius: '8px',
                  padding: '10px 14px',
                  justifyContent: 'center',
                  py: 4,
                })}
              >
                <InfoIcon sx={{ fontSize: 18, color: 'success.dark', mt: '2px' }} />
                <Typography variant="body2" color="success.dark">
                  No hay piezas seleccionadas
                </Typography>
              </Box>
            ) : (
              <>
                {hasMixedOrigins && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Todas las piezas deben compartir la misma ubicación de origen.
                  </Alert>
                )}
                <SectionTitle>
                  Piezas seleccionadas
                  <Chip
                    label={`${selectedProducts.length} ${selectedProducts.length === 1 ? 'pieza' : 'piezas'}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </SectionTitle>
                {sourceLocationName && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Origen: <strong>{sourceLocationName}</strong>
                  </Typography>
                )}
                <ProductListContainer>
                  {selectedProducts.map((product) => (
                    <ProductItem key={product.id}>
                      <ProductInfo>
                        <ProductCode>{product.id}</ProductCode>
                        <ProductName>{product.name}</ProductName>
                        <ProductDetails>
                          <ProductDetailChip label={product.type} size="small" />
                          {product.locationName && (
                            <ProductDetailChip label={product.locationName} size="small" />
                          )}
                        </ProductDetails>
                      </ProductInfo>
                      {onRemoveProduct && (
                        <RemoveButton
                          onClick={() => handleRemoveProduct(product.id)}
                          aria-label={`Quitar ${product.name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </RemoveButton>
                      )}
                    </ProductItem>
                  ))}
                </ProductListContainer>
              </>
            )}
          </StepContent>
        );

      case 1:
        return (
          <StepContent>
            <FormSectionTitle>Destino de la solicitud</FormSectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Las piezas se solicitarán hacia la sucursal seleccionada. Por defecto se usa su
              sucursal asignada.
            </Typography>

            {isSameAsSource && (
              <Alert severity="error" sx={{ mb: 2 }}>
                El destino no puede ser la misma ubicación que el origen de las piezas.
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormSection>
                  <FormLabel>
                    Ubicación destino
                    <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                      *
                    </Typography>
                  </FormLabel>
                  <StyledFormControl
                    fullWidth
                    select
                    value={destinationLocationId}
                    onChange={handleDestinationChange}
                    variant="outlined"
                    error={isSameAsSource}
                  >
                    <MenuItem value="">
                      <em>Seleccionar sucursal</em>
                    </MenuItem>
                    {availableDestinations.map((location) => (
                      <MenuItem key={location.id} value={location.id.toString()}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </StyledFormControl>
                  {availableDestinations.length === 0 && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      No hay sucursales disponibles como destino. Verifique su sucursal asignada.
                    </Typography>
                  )}
                </FormSection>
              </Grid>
              <Grid item xs={12}>
                <FormSection>
                  <FormLabel>Notas para el origen (opcional)</FormLabel>
                  <StyledTextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Indique detalles relevantes para quien aprueba la solicitud"
                    variant="outlined"
                  />
                </FormSection>
              </Grid>
            </Grid>
          </StepContent>
        );

      case 2:
        return (
          <StepContent>
            <SummaryContainer>
              <SummaryTitle>Resumen de la solicitud</SummaryTitle>
              <SummaryGrid>
                <SummaryCard>
                  <Box sx={{ flex: 1 }}>
                    <SummaryLabel>Ubicación origen</SummaryLabel>
                    <SummaryValueText>
                      <SummaryChip label={sourceLocationName || '—'} size="small" />
                    </SummaryValueText>
                  </Box>
                </SummaryCard>
                <SummaryCard>
                  <Box sx={{ flex: 1 }}>
                    <SummaryLabel>Ubicación destino</SummaryLabel>
                    <SummaryValueText>
                      <SummaryChip label={getDestinationLocationName() || '—'} size="small" />
                    </SummaryValueText>
                  </Box>
                </SummaryCard>
                <SummaryCard>
                  <Box sx={{ flex: 1 }}>
                    <SummaryLabel>Piezas solicitadas</SummaryLabel>
                    <SummaryValueText>{selectedProducts.length}</SummaryValueText>
                  </Box>
                </SummaryCard>
                <SummaryCard>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SummaryLabel>Notas</SummaryLabel>
                    <SummaryNotesText className={!notes ? 'empty' : ''}>
                      {notes || 'Sin notas'}
                    </SummaryNotesText>
                  </Box>
                </SummaryCard>
              </SummaryGrid>
            </SummaryContainer>

            {selectedProducts.length > 0 && (
              <Box>
                <ProductsTableTitle>Piezas incluidas</ProductsTableTitle>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={(theme) => ({
                    borderRadius: '12px',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  <StyledTable>
                    <StyledTableHead>
                      <TableRow>
                        <StyledTableHeadCell>Código</StyledTableHeadCell>
                        <StyledTableHeadCell>Nombre</StyledTableHeadCell>
                        <StyledTableHeadCell>Ubicación</StyledTableHeadCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {selectedProducts.map((product) => (
                        <StyledTableRow key={product.id}>
                          <StyledTableCell>{product.id}</StyledTableCell>
                          <StyledTableCell>{product.name}</StyledTableCell>
                          <StyledTableCell>{product.locationName || '—'}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </Box>
            )}
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />

      <PanelOverlay className={open ? 'open' : 'closed'}>
        <PanelContainer className={open ? 'open' : 'closed'}>
          <PanelHeader>
            <PanelTitle>Solicitar piezas de inventario</PanelTitle>
            <CloseButton onClick={handleCloseModal} aria-label="Cerrar panel">
              <CloseIcon />
            </CloseButton>
          </PanelHeader>

          <PanelContent>
            {error && <ErrorAlert severity="error">{error}</ErrorAlert>}

            <StepsContainer>
              <CompactProgressBarContainer>
                <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
              </CompactProgressBarContainer>
              <CompactStepIndicatorsContainer>
                <StepIndicators
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  steps={requestSteps}
                />
              </CompactStepIndicatorsContainer>
            </StepsContainer>

            {loading ? (
              <LoadingContainer>
                <CircularProgress size={40} />
                <LoadingText>Procesando solicitud...</LoadingText>
              </LoadingContainer>
            ) : (
              renderStepContent(activeStep)
            )}
          </PanelContent>

          <PanelActions>
            {activeStep === 0 && (
              <SecondaryButton variant="tonal" onClick={handleCloseModal}>
                Cancelar
              </SecondaryButton>
            )}
            {activeStep > 0 && (
              <SecondaryButton variant="tonal" onClick={handleBack}>
                Anterior
              </SecondaryButton>
            )}
            {activeStep < totalSteps - 1 ? (
              <PrimaryButton onClick={handleNext} disabled={!canProceedToNext()}>
                Siguiente
              </PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={loading || !canProceedToNext()}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Procesando...' : 'Confirmar solicitud'}
              </PrimaryButton>
            )}
          </PanelActions>
        </PanelContainer>

        <ConfirmationModal
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={executeRequest}
          title="¿Confirmar solicitud?"
          message="Las piezas quedarán en estado En solicitud hasta que el origen las apruebe o rechace."
          confirmText="Confirmar"
          cancelText="Cancelar"
          icon={<CheckCircleIcon />}
          variant="default"
          loading={loading}
        />
      </PanelOverlay>
    </>
  );
};

export default InventoryRequestModal;
